import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ClipboardDocumentIcon,
  KeyIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  useDeleteDkim,
  useDkim,
  useDomains,
  useGenerateDkim,
} from "@/api/hooks";
import { ApiError, type DkimKey } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const schema = z.object({
  domain: z.string().min(3),
  selector: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[a-z0-9_-]+$/, "a-z, 0-9, _ or -"),
});

export default function DkimPage() {
  const { data, isLoading } = useDkim();
  const [freshKey, setFreshKey] = useState<DkimKey | null>(null);
  const [deleteFor, setDeleteFor] = useState<DkimKey | null>(null);

  const byDomain = useMemo(() => {
    const map = new Map<string, DkimKey[]>();
    for (const k of data?.keys ?? []) {
      if (!map.has(k.domain)) map.set(k.domain, []);
      map.get(k.domain)!.push(k);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-heading text-3xl font-light sm:text-4xl">
            <span className="text-bitrefinery-red">D</span>KIM
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-bitrefinery-text text-body">
            Keys live in <span className="font-mono">/var/lib/rspamd/dkim/</span> and are
            signed into every outbound message by the rspamd milter. After generating a
            new selector you must paste its TXT record into DNS before it will verify.
          </p>
        </div>
        <GenerateDkimDialog onCreated={setFreshKey} />
      </div>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {byDomain.length === 0 && !isLoading && (
        <div className="rounded-2xl border border-dashed p-10 text-center text-muted-foreground">
          No DKIM keys yet. Generate one for each domain to sign outbound mail.
        </div>
      )}

      <div className="space-y-4">
        {byDomain.map(([domain, keys]) => (
          <div
            key={domain}
            className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
          >
            <div className="flex items-center justify-between border-b bg-muted/50 px-4 py-2">
              <div className="flex items-center gap-2 font-mono text-sm">
                <KeyIcon className="h-4 w-4 text-muted-foreground" />
                {domain}
                {!keys[0].known && (
                  <Badge variant="outline" className="text-[10px]">
                    orphaned
                  </Badge>
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                {keys.length} selector{keys.length === 1 ? "" : "s"}
              </span>
            </div>
            <ul className="divide-y">
              {keys.map((k) => (
                <li key={k.selector} className="space-y-2 px-4 py-3">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-mono text-sm">{k.selector}</div>
                      <div className="text-xs text-muted-foreground">
                        DNS name: <span className="font-mono">{k.recordName}</span>
                        {k.createdAt && (
                          <> · created {new Date(k.createdAt * 1000).toLocaleDateString()}</>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteFor(k)}
                      title="Delete key"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                  {k.txt ? (
                    <TxtRecordBlock value={k.txt} />
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      TXT file missing — regenerate the selector.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {freshKey && (
        <Dialog open onOpenChange={(o) => !o && setFreshKey(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>DKIM key generated</DialogTitle>
              <DialogDescription>
                Create a TXT record at{" "}
                <span className="font-mono">{freshKey.recordName}</span> containing the
                value below, then wait for DNS to propagate (usually a few minutes). Until
                the record is live, messages will be signed but fail verification.
              </DialogDescription>
            </DialogHeader>
            <TxtRecordBlock value={freshKey.txt ?? ""} />
            <DialogFooter>
              <Button onClick={() => setFreshKey(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {deleteFor && (
        <DeleteDkimDialog
          k={deleteFor}
          onOpenChange={(o) => !o && setDeleteFor(null)}
        />
      )}
    </div>
  );
}

function TxtRecordBlock({ value }: { value: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-slate-900 p-3 font-mono text-xs text-slate-100">
      <pre className="flex-1 whitespace-pre-wrap break-all">{value}</pre>
      <Button
        variant="ghost"
        size="icon"
        className="text-slate-100 hover:bg-slate-800 hover:text-slate-100"
        onClick={() => navigator.clipboard.writeText(value)}
        title="Copy"
      >
        <ClipboardDocumentIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

function GenerateDkimDialog({
  onCreated,
}: {
  onCreated: (k: DkimKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const create = useGenerateDkim();
  const domains = useDomains();
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      selector: new Date().getFullYear().toString(),
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4" />
          New selector
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate DKIM key</DialogTitle>
          <DialogDescription>
            Creates a 2048-bit RSA key at{" "}
            <span className="font-mono">
              /var/lib/rspamd/dkim/&lt;domain&gt;.&lt;selector&gt;.key
            </span>
            . The matching public key TXT record is shown afterwards.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit(async (v) => {
            try {
              const res = await create.mutateAsync(v);
              form.reset({ domain: "", selector: new Date().getFullYear().toString() });
              setOpen(false);
              onCreated(res);
            } catch (err) {
              form.setError("selector", {
                message: err instanceof ApiError ? err.code : "failed",
              });
            }
          })}
        >
          <div className="space-y-1.5">
            <Label htmlFor="domain">Domain</Label>
            <select
              id="domain"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              {...form.register("domain")}
            >
              <option value="">— pick domain —</option>
              {domains.data?.domains.map((d) => (
                <option key={d.name} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="selector">Selector</Label>
            <Input
              id="selector"
              placeholder="2026"
              {...form.register("selector")}
            />
            {form.formState.errors.selector && (
              <p className="text-xs text-destructive">
                {form.formState.errors.selector.message}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDkimDialog({
  k,
  onOpenChange,
}: {
  k: DkimKey;
  onOpenChange: (open: boolean) => void;
}) {
  const del = useDeleteDkim();
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete DKIM selector?</DialogTitle>
          <DialogDescription>
            Deletes <span className="font-mono">{k.selector}</span> for{" "}
            <span className="font-mono">{k.domain}</span>. Outbound mail will fail to sign
            for that selector. Remove the matching DNS TXT record to avoid confusing remote
            validators; keep it for a grace period if mail signed with the old key may still
            be in transit.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={async () => {
              await del.mutateAsync({ domain: k.domain, selector: k.selector });
              onOpenChange(false);
            }}
            disabled={del.isPending}
          >
            {del.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
